import { useLingui } from '@lingui/react/macro';

import { isAiEnabledForFindingType } from '../../annotation-utils/annotation-utils';
import type { ModelsToggles } from '../../findingSidebar/atoms';
import { useFindingSidebarTranslations } from '../../findingSidebar/constants';
import type {
  AnalysisErrorMessagingCode,
  FindingObject,
  ImageObject,
  Models,
  UserAnnotation,
} from '../../types';
import { FailedAnalysisType, getIsUserAnnotation } from '../../types';
import {
  getCariesSubTypedFindings,
  useGetModelFeatureFlags,
} from '../../utils';

import { ErrorMessageCodes } from './constants';

// type-guard for ErrorMessageCodes
function isErrorMessageCodeKey(
  value: unknown,
): value is keyof typeof ErrorMessageCodes {
  // @ts-expect-error TS(2345): Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
  return Object.prototype.hasOwnProperty.call(ErrorMessageCodes, value);
}

export const computeVisibleFindings = (
  findings: (FindingObject | UserAnnotation)[] | undefined,
  enabledFindingTypesParam: any,
  modelsToggles: ModelsToggles,
): any => {
  return (findings ?? []).filter((finding) => {
    const isEnabled = enabledFindingTypesParam[getAdjustedFindingType(finding)];
    return (
      // Always show user annotations (when restorative toggle is enabled), regardless of patient/clinical view
      (modelsToggles.caries && getIsUserAnnotation(finding)) ||
      (isEnabled && finding.validation !== 'Disagree')
    );
  });
};

function getAdjustedFindingType(f: any) {
  if (f.type === 'caries') {
    if (f.depth === 'into_enamel') {
      return 'incipients';
    } else {
      return 'caries';
    }
  }
  return f.type;
}

export const getPathologiesToDisplay = (
  enabledFindingTypes: any,
  pathologies: any,
) => {
  const {
    cariesFindings = [],
    prlFindings = [],
    calculusFindings = [],
    restorativeAdjacentLucencyFindings = [],
    userAnnotations = [],
  } = pathologies;
  let cariesSystemFindings = [];
  let areaOfConcernFindings: any = [];
  let incipientFindings: any = [];
  if (enabledFindingTypes.area_of_concern) {
    const { caries, areasOfConcern, incipients } =
      getCariesSubTypedFindings(cariesFindings);
    cariesSystemFindings = caries;
    areaOfConcernFindings = areasOfConcern;
    incipientFindings = incipients;
  } else {
    cariesSystemFindings = cariesFindings;
  }
  return [
    ...(enabledFindingTypes.restorative_adjacent_lucency
      ? restorativeAdjacentLucencyFindings
      : []),
    ...(enabledFindingTypes.caries ? cariesSystemFindings : []),
    ...(enabledFindingTypes.incipients ? incipientFindings : []),
    ...(enabledFindingTypes.periapical_radiolucency ? prlFindings : []),
    ...(enabledFindingTypes.calculus ? calculusFindings : []),
    ...areaOfConcernFindings,
    ...userAnnotations,
  ];
};

/**
 * VCA_FAILED) If VCA fails: Videa Caries Assist: No caries results shown due to analysis failure
 * RBL_FAILED) If RBL fails: Videa Perio Assist: No RBL results shown due to analysis failure
 * PA_FAILED) If PA caries fails: Videa Dental AI: No caries results shown due to analysis failure
 * PRL_FAILED) If PRL fails: Videa Dental AI: No PRL results shown due to analysis failure
 * CALCULUS_FAILED) If calculus fails: Videa Dental AI: No calculus results shown due to analysis failure
 * TDIS_FAILED) If tooth detection and identification fails: Videa AI: Some AI results not shown due to analysis failure
 *    NOTE: We may need to skip RBL also if TDIS fails. TBD based on what the results look like in the UI of RBL running without TDIS
 * CARIES_ONLY) If tooth thirds, caries subclassification, or tooth structure loss models fail: Videa AI: All AI-detected caries boxes detected by AI are displayed, but some details (e.g., tooth decay) may not appear due to analysis failure
 * TWO_FAILURES) If two separate steps from the above list (a-g) fail, show both error messages for those steps
 * MORE_THAN_TWO_FAILURES) If more than two separate steps from the above list (a-g) fail but some run and succeed, show generic error message: Videa Assist: Some AI results not shown due to analysis failure
 * FAILED) If none succeed, show generic error message: Videa AI: No AI results shown due to analysis failure
 */
export const computeErrorMessageToDisplay = (
  analysesFailed: FailedAnalysisType[],
  analysisErrorMessagingCode: AnalysisErrorMessagingCode[],
) => {
  // indicates all models have failed
  if (analysesFailed?.length >= 8) {
    return ErrorMessageCodes.FAILED;
  }
  // If more than 2 models with different messaging codes failed
  // If two separate steps from the above list (a-g) fail, show both error messages for those steps
  if (analysisErrorMessagingCode.length >= 2) {
    return ErrorMessageCodes.MULTIPLE_FAILURES;
  }

  // If only one model failed, display error message for that model
  if (analysisErrorMessagingCode.length < 2) {
    const errorCode = analysisErrorMessagingCode[0];

    if (isErrorMessageCodeKey(errorCode)) {
      return ErrorMessageCodes[errorCode];
    }

    return ErrorMessageCodes.FAILED;
  }
  return null;
};

export const getModelErrorMessage = (activeImage: ImageObject) => {
  if (
    activeImage?.videaError?.analysesFailed &&
    activeImage.videaError.analysesFailed.length > 0
  ) {
    const errorMessage = computeErrorMessageToDisplay(
      activeImage.videaError.analysesFailed,
      activeImage?.videaError.analysisErrorMessagingCode,
    );
    return errorMessage;
  }
  return null;
};

type AiFindingTypesKeys = keyof Models | 'area_of_concern';
type FindingTypeToFailedAnalysisType = {
  [key in AiFindingTypesKeys]: FailedAnalysisType[] | null;
};
type EnabledModelsMap = {
  [key in AiFindingTypesKeys]: boolean;
};

const findingTypeToFailedAnalysisMap: FindingTypeToFailedAnalysisType = {
  area_of_concern: [FailedAnalysisType.CARIES_SUBCLASSIFICATION],
  calculus: [FailedAnalysisType.CALCULUS],
  caries: [
    FailedAnalysisType.CARIES_BW,
    FailedAnalysisType.CARIES_PA,
    FailedAnalysisType.VIDEA_DENTAL_ASSIST_CARIES,
  ],
  periapical_radiolucency: [FailedAnalysisType.PERIAPICAL_RADIOLUCENCY],
  radiographic_boneloss: [FailedAnalysisType.RADIOGRAPHIC_BONE_LOSS],
  tooth_identification: [FailedAnalysisType.TOOTH_IDENTIFICATION],
  restorative_adjacent_lucency: [
    FailedAnalysisType.RESTORATIVE_ADJACENT_LUCENCY,
  ],
};

export const useComputeMessageForFindings = () => {
  const {
    isPrlEnabled,
    isRblEnabled,
    isCalculusEnabled,
    isCariesSubclassEnabled,
    isVdaEnabled,
  } = useGetModelFeatureFlags();
  const { typeToHeaderMap } = useFindingSidebarTranslations();
  const { t } = useLingui();

  const computeMessagingForNoAiFindings = (activeImage: ImageObject) => {
    // If model FF is disabled we will not show a message for the model
    const enabledModelsMap: EnabledModelsMap = {
      calculus: isCalculusEnabled,
      caries: true,
      periapical_radiolucency: isPrlEnabled,
      radiographic_boneloss: isRblEnabled,
      area_of_concern: isCariesSubclassEnabled,
      // Don't display tooth_identification failure for now
      tooth_identification: false,
      restorative_adjacent_lucency: isVdaEnabled,
    };

    // Build enabled models array
    const enabledModels: AiFindingTypesKeys[] = [];
    Object.entries(enabledModelsMap).reduce((accumulator, [key, value]) => {
      if (value) {
        accumulator.push(key as AiFindingTypesKeys);
        return accumulator;
      }
      return accumulator;
    }, enabledModels);

    // Loop through enabled models, creating an array w/ all findings messages
    let noFindingsMessages = enabledModels.reduce(
      (accumulator, findingGroupType) => {
        // For areas of concern we need to dig into caries to determine if we have findings or not (isCaries === false)
        const hasAreaOfConcernFindings =
          findingGroupType === 'area_of_concern' &&
          activeImage?.models?.caries?.findings?.some(
            (finding) => finding.isCaries === false,
          );

        const hasFindings =
          // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
          activeImage?.models?.[findingGroupType]?.findings?.length > 0 ||
          hasAreaOfConcernFindings;
        // Determine if there is a matching error for the model
        // @ts-expect-error TS(2531): Object is possibly 'null'.
        const hasModelError = findingTypeToFailedAnalysisMap[
          findingGroupType
        ].some((failedAnalysis) =>
          activeImage?.videaError?.analysesFailed?.includes(failedAnalysis),
        );
        // If we have findings or a model error, do not show a message for this model
        if (hasFindings || hasModelError) {
          return accumulator;
        }

        let messageToDisplay = '';
        const hasModelRun = isAiEnabledForFindingType(
          activeImage,
          findingGroupType,
        );
        // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
        const modelNotRunMessaging = t`Analysis for ${typeToHeaderMap[findingGroupType]} was not included when this image was processed by Videa’s AI`;

        // For now, if we have no caries findings, we cannot be certain that the areas of concern model ran or not
        if (!hasModelRun) {
          messageToDisplay = modelNotRunMessaging;
        } else {
          // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
          messageToDisplay = t`No findings detected for ${typeToHeaderMap[findingGroupType]}`;
        }

        // @ts-expect-error TS(2345): Argument of type 'string' is not assignable to par... Remove this comment to see the full error message
        accumulator.push(messageToDisplay);
        return accumulator;
      },
      [],
    );

    // Add model errors to list
    const modelErrors = activeImage?.videaError?.analysisErrorMessagingCode.map(
      (errorCode) => {
        if (isErrorMessageCodeKey(errorCode)) {
          return ErrorMessageCodes[errorCode];
        }

        return ErrorMessageCodes.FAILED;
      },
    );

    if (modelErrors) {
      // @ts-expect-error TS(2769): No overload matches this call.
      noFindingsMessages = noFindingsMessages.concat(modelErrors);
    }

    return { noFindingsMessages, hasModelErrors: !!modelErrors?.length };
  };
  return computeMessagingForNoAiFindings;
};
